// Info pane rendering for the workbench panel. Extracted from
// workbench-ui.ts to stay within the §10 400-line module cap.
//
// The pane has two display modes:
//   - empty:   "Hover a recipe to see details" placeholder
//   - recipe:  recipe name + per-input have/need rows + footer details
//
// Pure render functions: take a parent Container + the data + a fixed
// rect, produce side-effects on the parent (Pixi mutations are inherent
// to the framework). The container's first child is always the frame
// graphic — clearPaneContent leaves it intact and tears down everything
// else.

import { Container, Graphics, Text } from "pixi.js";
import { getItemDef } from "../../shared/items.ts";
import type { Recipe } from "../../shared/recipes.ts";
import type { MaterialsResult } from "./workbench-materials.ts";

const PAD = 12; // contracts:allow-literal — inner padding px
const INFO_BG = 0x0e0e22;
const INFO_BORDER = 0x334499;
const TITLE_COLOR = 0xeeeeff;
const MUTED_COLOR = 0x666688;
const LOCK_ORANGE = 0xcc6633;
const BODY_COLOR = 0xaaaacc;
const MAT_OK = 0xaaeebc;
const MAT_SHORT = 0xff8844;
const BODY_SIZE = 11; // contracts:allow-literal — body font size px
const INFO_SIZE = 12; // contracts:allow-literal — name font size px

export type InfoPaneHandle = {
  pane: Container;
  rect: { x: number; y: number; w: number; h: number };
};

// Build the empty pane (frame + placeholder). Caller stores the handle
// and feeds it into renderRecipe / renderEmpty on selection changes.
export function createInfoPane(
  parent: Container,
  x: number,
  y: number,
  width: number,
  height: number,
): InfoPaneHandle {
  const pane = new Container();
  pane.x = x;
  pane.y = y;
  parent.addChild(pane);
  pane.addChild(
    new Graphics()
      .rect(0, 0, width, height)
      .fill({ color: INFO_BG })
      .stroke({ color: INFO_BORDER, width: 1 }), // contracts:allow-literal
  );
  const handle: InfoPaneHandle = { pane, rect: { x, y, w: width, h: height } };
  renderEmpty(handle);
  return handle;
}

// Tears down everything except the frame (first child). Used by both
// render functions before they re-populate.
function clearContent(handle: InfoPaneHandle): void {
  const { pane } = handle;
  while (pane.children.length > 1) {
    const last = pane.children[pane.children.length - 1];
    if (!last) break;
    pane.removeChild(last);
    last.destroy({ children: true });
  }
}

export function renderEmpty(handle: InfoPaneHandle): void {
  clearContent(handle);
  const hint = new Text({
    text: "Hover a recipe to see details",
    style: { fontSize: INFO_SIZE, fill: MUTED_COLOR, fontFamily: "monospace", fontStyle: "italic" },
  });
  hint.x = PAD;
  hint.y = PAD / 2; // contracts:allow-literal — top pad
  handle.pane.addChild(hint);
}

export function renderRecipe(
  handle: InfoPaneHandle,
  recipe: Recipe,
  unlocked: boolean,
  materials: MaterialsResult,
  batchCount: number,
  skillXpMap: Record<string, number>,
): void {
  clearContent(handle);
  const { pane } = handle;
  let cy = PAD / 2; // contracts:allow-literal — top pad

  const nameTxt = new Text({
    text: recipe.name,
    style: {
      fontSize: INFO_SIZE,
      fill: unlocked ? TITLE_COLOR : LOCK_ORANGE,
      fontFamily: "monospace",
      fontWeight: "bold",
    },
  });
  nameTxt.x = PAD;
  nameTxt.y = cy;
  pane.addChild(nameTxt);
  cy += nameTxt.height + 4; // contracts:allow-literal — name → details gap

  const inputsHeader = new Text({
    text: "Materials:",
    style: { fontSize: BODY_SIZE, fill: BODY_COLOR, fontFamily: "monospace" },
  });
  inputsHeader.x = PAD;
  inputsHeader.y = cy;
  pane.addChild(inputsHeader);
  cy += inputsHeader.height + 2; // contracts:allow-literal — small gap

  for (const row of materials.perInput) {
    const itemName = getItemDef(row.itemId)?.name ?? row.itemId.replace(/_/g, " ");
    const line = new Text({
      text: `  ${itemName}: have ${row.have} / need ${row.need}`,
      style: {
        fontSize: BODY_SIZE,
        fill: row.sufficient ? MAT_OK : MAT_SHORT,
        fontFamily: "monospace",
      },
    });
    line.x = PAD;
    line.y = cy;
    pane.addChild(line);
    cy += line.height + 1; // contracts:allow-literal — tight per-input rows
  }
  cy += 2; // contracts:allow-literal — gap before footer details

  const secs = recipe.craftTimeMs / 1000; // contracts:allow-literal — ms to seconds
  const outputName = getItemDef(recipe.outputBaseId)?.name ?? recipe.outputBaseId.replace(/_/g, " ");
  const detailsLines: string[] = [];
  detailsLines.push(`Output: 1× ${outputName} (quality rolled at completion)`);
  detailsLines.push(`Craft time: ${secs}s per item${batchCount > 1 ? ` (×${batchCount})` : ""}`);
  if (recipe.skillGates.length > 0) {
    const parts = recipe.skillGates.map((gate) => {
      const have = skillXpMap[gate.skillId] ?? 0;
      const met = have >= gate.minXp;
      return `${met ? "✓" : "✗"} ${gate.skillId} ≥ ${gate.minXp} (have ${Math.floor(have)})`;
    });
    detailsLines.push(`Requires: ${parts.join(", ")}`);
  }
  const detailsText = new Text({
    text: detailsLines.join("\n"),
    style: { fontSize: BODY_SIZE, fill: BODY_COLOR, fontFamily: "monospace" },
  });
  detailsText.x = PAD;
  detailsText.y = cy;
  pane.addChild(detailsText);
}

// Color constants exposed for tests / docs that want to inspect the
// material-row palette without instantiating the pane.
export const WORKBENCH_INFO_COLORS = { matOk: MAT_OK, matShort: MAT_SHORT } as const;
